import { RadioGroup } from '@headlessui/react'
import { Button } from '@progress/kendo-react-buttons'
import { classNames } from '@progress/kendo-react-common'
import DataTable from 'components/DataTable'
import ConfirmDialogModal from 'components/Modals/ConfirmDialog'
import { GlobalDialogType } from 'components/Modals/GlobalDialog'
import Pagination, { IPagination } from 'components/Pagination/Pagination'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { useEffect, useState } from 'react'
import { MembershipApiItem } from 'types/membership'
import { MembershipClient } from 'types/membership-client'
import { mapToOrderDTO, OrderDTO, OrderList, OrderPaymentMethod } from 'types/order'
import { postApiRequest, putApiRequest, useFetch } from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import { toHourMinute, toCurrency } from 'utils/string-localization-helper'
import { FetchMethods } from 'utils/types'

enum PaymentMethodRadioOptions { //replace with OrderPaymentMethod when we implement payment for bank transfers
    CASH = "CASH",
    CARD = "CARD"
}

function EmptyOrders() {
    return (
        <div className="text-center py-10">
            <div className="rounded-full bg-gray-100 w-20 h-20 inline-block leading-loose text-center align-middle">
                <i className="text-gray-400 feather feather-calendar text-4xl leading-loose" />
            </div>
            <div className="text-gray-400 block mt-4 mx-auto">
                This client currently has no orders
            </div>
        </div>
    )
}

function RenderOrders(props: any) {
    const orders: OrderList[] = props.orders
    const translations: Record<string, string> = props.translations
    const rows: HTMLTableRowElement[] = (orders || []).map(
        (
            order: OrderList,
            idx
        ) =>
            (
                <tr
                    key={order.id}
                    className={classNames(
                        idx % 2 ? 'bg-gray-50' : '',
                        'hover:bg-blue-300 hover:bg-opacity-30'
                    )}
                >
                    {' '}
                    <td
                        className="px-3 py-3 align-middle whitespace-nowrap"
                        onClick={() => props.handleEdit(idx)}
                    >
                        <span className="block">
                            {new Date(order.date).toLocaleString(undefined, {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                            })}
                        </span>
                        <span className="block">
                            {toHourMinute(new Date(order.time))}
                        </span>
                    </td>
                    <td
                        className="px-3 py-3 align-middle whitespace-nowrap"
                        onClick={() => props.handleEdit(idx)}
                    >
                        <span className="block">
                            {toCurrency(order.total)}
                        </span>
                    </td>
                    <td
                        className="px-3 py-3 align-middle whitespace-nowrap"
                        onClick={() => props.handleEdit(idx)}
                    >
                        <span className="block">{order.payment_method}</span>
                    </td>
                    <td
                        className="px-3 py-3 align-middle whitespace-nowrap flex-flex-col"
                        onClick={() => props.handleEdit(idx)}
                    >
                        <div className='flex flex-row'>
                            <i
                                className={classNames(
                                    'mr-2',
                                    order.status ? 'text-green-500 feather-check' : 'feather-x text-[#bcbec0]'
                                )}
                                aria-hidden="true"
                            />
                            <span className="mr-2">
                                <Translation
                                    content_key={order.status ? 'paid' : 'unpaid'}
                                />

                            </span>
                        </div>
                        {!order.status &&
                            <button className="text-xxs uppercase text-orange-500"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    props.setSelectedOrder(order)
                                }}
                            >
                                <Translation
                                    content_key={'clients.pay_now'}
                                />
                            </button>
                        }
                    </td>
                    <td
                        className="px-3 py-3 align-middle whitespace-nowrap"
                        onClick={() => props.handleEdit(idx)}
                    >
                        <span>{order.coupon}</span>
                    </td>
                    <td
                        className="px-3 py-3"
                        onClick={() => props.handleEdit(idx)}
                    >
                        <span>{order.item}</span>
                    </td>
                    <td className="px-3 py-3 align-middle whitespace-nowrap">
                        <Button
                            disabled={!order.status}
                            className={classNames("mr-2",
                                order.status ? "bg-red-100 text-red-500" : "bg-gray-100 text-gray-500 cursor-not-allowed"
                            )}
                            iconClass="feather-corner-up-left mr-1"
                            onClick={(e) => {
                                e.stopPropagation()
                                props.setSelectedOrder(order)
                            }}
                        >
                            <Translation content_key='clients.refund' />
                        </Button>
                        <Button
                            className="bg-primary-lighter bg-opacity-50 mr-2"
                            iconClass="feather-edit"
                            onClick={() => {
                                props.handleEdit(idx)
                            }}
                        ></Button>
                        <Button
                            className={classNames(
                                'bg-primary-lighter bg-opacity-50 mr-2',
                                order.payment_intent ? '' : 'hidden'
                            )}
                            iconClass="feather-eye"
                            title={getTranslation(
                                'order_view_tooltip',
                                translations
                            )}
                            onClick={() => props.handleView(idx)}
                        ></Button>
                    </td>
                </tr>
            ) as unknown as HTMLTableRowElement
    )

    const header_titles = [
        'date',
        'total',
        'payment_method',
        'status',
        'coupon',
        'item',
        'actions',
    ]
    const headers = header_titles.map((title) => {
        return {
            label: getTranslation(title, translations),
            classNames:
                'px-3 py-3 text-left text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap',
        }
    })

    return (
        <div className="flex flex-col">
            <div className="align-middle inline-block min-w-full">
                <div className="overflow-hidden">
                    <DataTable rows={rows} columns={headers} />
                    <Pagination {...props.paginationProps} />
                </div>
            </div>
        </div>
    )
}

export function Orders() {
    const { setGlobalError } = useAppErrorApiContext()
    const {
        translations,
        setGlobalLoading,
    } = useAppContext()
    const {
        all_memberships,
        services,
        locations,
        tenant,
    } = useTenantContext()
    const {
        CreateClientModal,
        OrderEditModal,
        GlobalDialog
    } = useAuth()
    const {
        attributes,
        setAttributes
    } = CreateClientModal
    const {
        attributes: order_attributes,
        setAttributes: setOrderAttributes
    } = OrderEditModal

    const [orders, setOrders] = useState<OrderList[]>([])
    const [pageSize, setPageSize] = useState(10)
    const [currentPage, setCurrentPage] = useState(0)
    const [selected_order, setSelectedOrder] = useState<OrderList | undefined>(undefined)
    const [payment_option, setPaymentOption] = useState<PaymentMethodRadioOptions>(PaymentMethodRadioOptions.CASH)

    // Client Orders
    const { data: client_orders_api_response, doFetch: getClientOrders } = useFetch(
        `/v1/orders/client-id?clientId=${attributes?.id}&size=${pageSize}&page=${currentPage}&sort=createdAt,desc`,
        FetchMethods.GET,
        !!attributes?.id
    )

    // Membership Clients
    const { data: memberships_client_api_response, doFetch: getMembershipClients } = useFetch(
        `/v1/memberships/clients?clientId=${CreateClientModal?.attributes?.id}&size=500`,
        FetchMethods.GET,
        false
    )

    // Membership Client Renewal List
    const {
        data: membership_client_renewal_api_response,
        doFetch: getMembershipClientRenewal,
    } = useFetch(`/v1/memberships/renewals/`, FetchMethods.GET, false)

    function getData(lookupId: string) {
        const fetched_memberships: MembershipApiItem[] = all_memberships
        const fetched_memberships_client: MembershipClient[] =
            attributes?.membership_clients as MembershipClient[]

        const membership_client = fetched_memberships_client.find(
            (item) => item.id === lookupId
        )
        const membership = fetched_memberships.find(
            (item) => item.id === membership_client?.membershipId
        )
        const service = services.find(
            (item) => item.id === lookupId
        )
        const location = locations.find(
            (item) => item.id == membership?.primaryLocationId
        )

        return {
            membership,
            service,
            location,
        }
    }

    useEffect(() => {
        if (client_orders_api_response?.content) {
            setAttributes({
                ...attributes,
                client_orders: client_orders_api_response?.content
                    || [],
            })
        }
    }, [client_orders_api_response])

    useEffect(() => {
        if (!attributes?.membership_clients) {
            getMembershipClients()
        }
    }, [attributes?.membership_clients, attributes?.id])

    useEffect(() => {
        if (memberships_client_api_response?.content) {
            CreateClientModal.setAttributes({
                ...CreateClientModal.attributes,
                membership_clients: memberships_client_api_response?.content
                    || []
            })
        }
    }, [memberships_client_api_response])

    useEffect(() => {
        if (
            !attributes?.client_orders ||
            !all_memberships?.length ||
            !attributes?.membership_clients ||
            !locations?.length ||
            !services?.length
        ) {
            return
        }

        const orders: OrderList[] = (attributes.client_orders as OrderDTO[]).map(
            (order_dto: OrderDTO) => {
                const {
                    id,
                    clientEmail,
                    clientId,
                    commission,
                    commissionAmount,
                    commissionPercentage,
                    couponCode,
                    couponId,
                    membershipClientId,
                    payments,
                    totalAmount,
                    createdAt,
                    fullyPaid,
                    lookupId,
                    paymentMethod,
                    quantity,
                    soldBy,
                    type
                } = order_dto

                const payment = payments[0]
                const payment_intent = JSON.parse(
                    payment.paymentProcessorResponse
                )?.payment_intent
                const { membership, service, location } = getData(lookupId)

                const order = {
                    client_email: clientEmail,
                    client_id: clientId,
                    commission,
                    commission_amount: commissionAmount,
                    commission_percentage: commissionPercentage,
                    coupon: couponCode,
                    coupon_id: couponId,
                    date: createdAt,
                    id,
                    item: `${membership?.name || service?.name} @ ${location?.name || 'All'
                        }`,
                    lookup_id: lookupId,
                    membership_client_id: membershipClientId,
                    payment_method: paymentMethod,
                    payment_intent,
                    quantity,
                    status: fullyPaid,
                    sold_by: soldBy,
                    time: createdAt,
                    total: totalAmount,
                    type,

                } as OrderList

                if (!membership && !service) {
                    getMembershipClientRenewal(null, `${lookupId}`)
                }
                return order
            }
        )
        setOrders(orders)
    }, [
        attributes?.client_orders,
        all_memberships,
        attributes?.membership_clients,
        locations,
        services
    ])

    useEffect(() => {
        if (membership_client_renewal_api_response?.id) {
            const old_orders = [...orders]
            const idx = old_orders.findIndex(
                (item) =>
                    item.lookup_id ==
                    membership_client_renewal_api_response.id
            )
            if (old_orders[idx]) {
                const { membership, service, location } = getData(
                    membership_client_renewal_api_response.membershipClientId
                )
                old_orders[idx].item = `${membership?.name || service?.name
                    } @ ${location?.name || 'All'}`
            }
            setOrders(old_orders)
        }
    }, [membership_client_renewal_api_response])

    useEffect(() => {
        if (attributes?.refetch_orders) {
            getClientOrders()
            setAttributes({
                ...attributes,
                refetch_orders: false
            })
        }
    }, [attributes?.refetch_orders])

    function handleEdit(idx: number) {
        setOrderAttributes({
            order: orders[idx]
        })
        OrderEditModal.open()
    }

    function handleView(idx: number) {
        const order = orders[idx]
        if (order && order.payment_intent) {
            window.open(
                `https://dashboard.stripe.com/payments/${order.payment_intent}`,
                '_blank',
                'noopener,noreferrer'
            )
        }
    }

    async function handleRefund() {
        if (selected_order && tenant?.id) {
            try {
                setGlobalLoading(true)
                let res = await postApiRequest(`/v1/refunds/order?orderId=${selected_order.id}&tenantId=${tenant.id}`)
                if (res?.ok) {
                    GlobalDialog.setAttributes({
                        title: getTranslation("clients.refund_success", translations, {
                            order_name: selected_order.item
                        }),
                        button: 'clients.back_to_orders',
                        className: 'w-auto',
                        type: GlobalDialogType.SUCCESS,
                    })
                    GlobalDialog.open()
                    getClientOrders()
                }
            } catch (err) {
                setGlobalError(err)
            } finally {
                setSelectedOrder(undefined)
                setGlobalLoading(false)
            }
        }

    }

    async function handlePayment() {
        if (selected_order && tenant?.id) {
            try {
                setGlobalLoading(true)
                let res
                switch (payment_option) {
                    case PaymentMethodRadioOptions.CASH:
                        const payload_cash = mapToOrderDTO({
                            ...selected_order,
                            status: true,
                            payment_amount: selected_order.total,
                            payment_method: OrderPaymentMethod.CASH,
                        })
                        res = await putApiRequest(`/v1/orders/${selected_order.id}`, payload_cash)
                        break
                    case PaymentMethodRadioOptions.CARD:
                        const payload_card = {
                            order: mapToOrderDTO({
                                ...selected_order,
                                status: true,
                                payment_amount: selected_order.total,
                                payment_method: OrderPaymentMethod.CARD,
                            }),
                            amount: selected_order.total,
                            savePaymentMethod: false,
                            tenantId: tenant.id
                        }
                        res = await putApiRequest(`/v1/payments/add-payment/${selected_order.id}`, payload_card)
                        break
                }

                if (res?.ok) {
                    GlobalDialog.setAttributes({
                        title: getTranslation("clients.payment_success", translations),
                        button: 'clients.back_to_orders',
                        className: 'w-auto',
                        type: GlobalDialogType.SUCCESS,
                    })
                    GlobalDialog.open()
                    getClientOrders()
                }
            } catch (err) {
                setGlobalError(err)
            } finally {
                setSelectedOrder(undefined)
                setGlobalLoading(false)
            }
        }
    }

    const paginationProps: IPagination = {
        totalPages: client_orders_api_response?.totalPages,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        totalRecords: client_orders_api_response?.totalElements,
    }

    return (
        <div>
            {orders.length == 0 ? (
                <EmptyOrders />
            ) : (
                <>
                    <RenderOrders
                        orders={orders}
                        translations={translations}
                        handleEdit={handleEdit}
                        handleView={handleView}
                        paginationProps={paginationProps}
                        setSelectedOrder={setSelectedOrder}
                    />
                    {!!selected_order && selected_order.status && (
                        <ConfirmDialogModal
                            prompt_message={getTranslation('clients.refund_order_prompt', translations, { order_name: selected_order.item || '' })}
                            onCancel={() => {
                                setSelectedOrder(undefined)
                            }}
                            onConfirm={() => handleRefund()}
                            body={
                                <div className='flex flex-col'>
                                    <Translation
                                        render_as='span'
                                        content_key='clients.refund_message_body'
                                        content_values={{
                                            client_name: `${attributes?.first_name} ${attributes?.last_name}`,
                                            re_amount: `${toCurrency(selected_order.total)}`
                                        }}
                                    />
                                    {selected_order.payment_intent &&
                                        <a className='text-primary-light cursor-pointer'
                                            onClick={() => {
                                                window.open(
                                                    `https://dashboard.stripe.com/payments/${selected_order.payment_intent}`,
                                                    '_blank',
                                                    'noopener,noreferrer'
                                                )
                                            }}
                                        >
                                            <Translation
                                                content_key='clients.refund_different_amount'
                                            />
                                        </a>
                                    }
                                </div>
                            }
                        />
                    )}
                    {!!selected_order && !selected_order.status && (
                        <ConfirmDialogModal
                            prompt_message={getTranslation('clients.mark_as_paid_prompt', translations, { order_name: selected_order.item || '' })}
                            onCancel={() => {
                                setSelectedOrder(undefined)
                            }}
                            onConfirm={() => handlePayment()}
                            body={
                                // TODO: uncomment out when we support other payment methods
                                <RadioGroup
                                    value={payment_option}
                                    onChange={(v: PaymentMethodRadioOptions) => {
                                        setPaymentOption(v)
                                    }}
                                >
                                    <div className='flex flex-col mt-4 mb-6 gap-y-2'>
                                        {/* {Object.values(PaymentMethodRadioOptions).map((option: PaymentMethodRadioOptions) => {
                                            return (
                                                <RadioGroup.Option key={option} value={option}>
                                                    {({ checked }) => (
                                                        <div className="flex items-center gap-2 text-base cursor-pointer">
                                                            <div
                                                                className={classNames(
                                                                    checked
                                                                        ? 'border-primary-light'
                                                                        : 'border-gray-400',
                                                                    'rounded-full w-6 h-6 border-2 flex items-center'
                                                                )}
                                                            >
                                                                <i
                                                                    className={classNames(
                                                                        checked
                                                                            ? 'bg-primary-light'
                                                                            : '',
                                                                        'rounded-full w-4 h-4 block m-auto'
                                                                    )}
                                                                />
                                                            </div>
                                                            <span
                                                                className={
                                                                    checked
                                                                        ? 'text-primary-light'
                                                                        : ''
                                                                }
                                                            >
                                                                <Translation content_key={`clients.pay_with_${option.toLowerCase()}`} />
                                                            </span>
                                                        </div>
                                                    )}
                                                </RadioGroup.Option>
                                            )
                                        })} */}
                                        <RadioGroup.Option key={PaymentMethodRadioOptions.CASH} value={PaymentMethodRadioOptions.CASH}>
                                            {({ checked }) => (
                                                <div className="flex items-center gap-2 text-base cursor-pointer">
                                                    <div
                                                        className={classNames(
                                                            checked
                                                                ? 'border-primary-light'
                                                                : 'border-gray-400',
                                                            'rounded-full w-6 h-6 border-2 flex items-center'
                                                        )}
                                                    >
                                                        <i
                                                            className={classNames(
                                                                checked
                                                                    ? 'bg-primary-light'
                                                                    : '',
                                                                'rounded-full w-4 h-4 block m-auto'
                                                            )}
                                                        />
                                                    </div>
                                                    <span
                                                        className={
                                                            checked
                                                                ? 'text-primary-light'
                                                                : ''
                                                        }
                                                    >
                                                        <Translation content_key={`clients.pay_with_cash`} />
                                                    </span>
                                                </div>
                                            )}
                                        </RadioGroup.Option>
                                    </div>
                                </RadioGroup>
                            }
                        />
                    )}
                </>
            )}
        </div>
    )
}

export default Orders
